import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { setRegionSuggestion } from "../../redux/actions";
import { AppState } from "../../redux/types";
import { formatRegionOption, REGIONS, RegionCode } from "../../utils/constants";
import { buildTranslatedPath, getLocaleFromPath } from "../../i18n/config";


const RegionBanner = () => {
    const dispatch = useDispatch();
    const { pathname, search } = useLocation();
    const suggestedCode = useSelector((state: AppState) => state.region.suggestion) as RegionCode | null;
    const currentCode = useSelector((state: AppState) => state.region.code) as RegionCode;
    const dismissed = localStorage.getItem("regionBannerDismissed");

    const [selected, setSelected] = useState<RegionCode>(suggestedCode ?? currentCode);
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Sync local selected state when suggestedCode resolves asynchronously
    useEffect(() => {
        if (suggestedCode) {
            setSelected(suggestedCode);
        }
    }, [suggestedCode]);

    if (!suggestedCode || suggestedCode === currentCode || dismissed) return null;

    const suggestedRegion = REGIONS.find((r) => r.code === suggestedCode);
    if (!suggestedRegion) return null;

    const currentRegion = REGIONS.find((r) => r.code === currentCode) ?? suggestedRegion;
    const selectedRegion = REGIONS.find((r) => r.code === selected)!;

    const handleApply = () => {
        const region = REGIONS.find((r) => r.code === selected);
        if (!region) return;

        const currentLang = getLocaleFromPath(pathname);
        const langChanging = region.lang !== currentLang;
        const newPath = langChanging ? buildTranslatedPath(pathname, region.lang, currentLang) + search : undefined;

        // Use centralized flow — LanguageChangeDialog handles everything
        window.dispatchEvent(
            new CustomEvent("regionChangeRequested", {
                detail: { regionCode: region.code, newLang: region.lang, prevLang: currentLang, newPath },
            }),
        );

        dispatch(setRegionSuggestion(null));
        localStorage.removeItem("regionSuggestion");
        localStorage.setItem("regionBannerDismissed", "true");
    };

    const handleDismiss = () => {
        dispatch(setRegionSuggestion(null));
        localStorage.removeItem("regionSuggestion");
        localStorage.setItem("regionBannerDismissed", "true");
    };

    return createPortal(
        <div className="region-banner">
            <span>
                Prices shown for {formatRegionOption(currentRegion)}. Not your region?
            </span>
            <div className="region-banner__dropdown" ref={dropdownRef}>
                <button
                    className="region-banner__dropdown-trigger"
                    onClick={() => setOpen((o) => !o)}
                >
                    {formatRegionOption(selectedRegion)}  {"▾"}
                </button>
                {open && (
                    <div className="region-banner__dropdown-menu">
                        {REGIONS.map((r) => (
                            <button
                                key={r.code}
                                className={`region-banner__dropdown-item${r.code === selected ? " active" : ""}`}
                                onClick={() => { setSelected(r.code as RegionCode); setOpen(false); }}
                            >
                                {formatRegionOption(r)}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <button className="region-banner__accept" onClick={handleApply}>
                Apply
            </button>
            <button className="region-banner__dismiss" onClick={handleDismiss}>
                Dismiss
            </button>
        </div>,
        document.body,
    );
};

export default RegionBanner;
